import type { FC, PropsWithChildren } from 'react'
import { useTheme } from '@emotion/react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import NavigationV2 from '@segment/templates/Navigation2'
import DividerSection from '@segment/templates/DividerSection'

import { useCategoryQuery } from '@segment/api/routes/dataHub/queries'

import Layout from '@segment/next-shared/layout'

import Content from './components/Content'
import Hero from './components/Hero'
import Definition from './components/Definition'
import Learn from './components/Learn'
import CustomSection from './components/CustomSection'
import FaqSection from './components/FaqSection'

export interface DataHubCategoryProps {
  categorySlug: string
}

const DataHubCategory: FC<PropsWithChildren<DataHubCategoryProps>> = ({ categorySlug }) => {
  const { data } = useCategoryQuery({ args: [categorySlug] })

  const theme = useTheme()
  const customSectionIsPresent =
    data?.customSectionOverline ||
    data?.customSectionHeading ||
    data?.customSectionDescription ||
    data?.customSectionLinkText ||
    data?.customSectionLinkUrl
  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={`${data?.seoTitle} | Twilio Segment`}
        pageDescription={data?.seoDescription}
        pageOG={data?.seoImage}
        bodyBackground={theme.palette.neutrals.white[100]}
      >
        <NavigationV2 />
        <main>
          <Hero heading={data?.title} description={data?.description} image={data?.heroImage} />
          {data?.title && data?.definition && (
            <Definition categoryName={data.title} categoryDefinitions={data.definition} />
          )}
          {data?.cards && <Learn cards={data?.cards} />}
          {customSectionIsPresent && (
            <CustomSection
              overline={data?.customSectionOverline}
              heading={data?.customSectionHeading}
              description={data?.customSectionDescription}
              linkText={data?.customSectionLinkText}
              linkUrl={data?.customSectionLinkUrl}
            />
          )}
          {(data?.mainContent || data?.faqs) && customSectionIsPresent && <DividerSection />}
          {data?.mainContent && <Content data={data.mainContent} />}
          {data?.faqs && <FaqSection data={data?.faqs} />}
        </main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

export default DataHubCategory
